import React from "react";
import Box from "@/ui/atoms/Box";
import { Icon, IconBox, Tooltip } from "@proda-ai/ui-kit";

import type { BoxProps } from "@/ui/atoms/Box";

export type UserGroupIndicatorProps = BoxProps & {
    users: {
        email: string;
    }[];
};

const UserGroupIndicator: React.FC<UserGroupIndicatorProps> = ({
    users,
    sx,
    ...otherProps
}) => {
    const renderIcons = () => {
        const count = users.length;
        if (count === 0) {
            return null;
        }

        return (
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    ...sx,
                }}
                {...otherProps}
            >
                <IconBox mr={0.5}>
                    <Icon
                        icon="triangle-exclamation"
                        color="var(--proda-black)"
                        size="sm"
                    />
                </IconBox>
                <Tooltip
                    content={
                        <Box
                            sx={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            {users[0]?.email}
                        </Box>
                    }
                >
                    <Box
                        sx={{
                            position: "relative",
                            zIndex: 1,
                            backgroundColor: "var(--proda-yellow-20)",
                            borderRadius: "50%",
                            padding: "2px",
                            boxShadow: "1px 2px 6px rgba(6, 10, 22, 0.25)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            width: "24px",
                            height: "24px",
                        }}
                    >
                        <IconBox>
                            <Icon
                                icon="user"
                                color="var(--proda-black)"
                                size="xs"
                            />
                        </IconBox>
                    </Box>
                </Tooltip>
                {count > 1 && (
                    <Tooltip
                        content={
                            <Box
                                sx={{
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                }}
                            >
                                {users[1]?.email}
                            </Box>
                        }
                        style={{
                            width: "100%",
                            height: "100%",
                        }}
                    >
                        <Box
                            sx={{
                                position: "relative",
                                marginLeft: "-8px",
                                backgroundColor: "var(--proda-yellow-20)",
                                borderRadius: "50%",
                                padding: "2px",
                                boxShadow: "1px 2px 6px rgba(6, 10, 22, 0.25)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                                width: "24px",
                                height: "24px",
                            }}
                        >
                            <IconBox>
                                <Icon
                                    icon="user"
                                    color="var(--proda-black)"
                                    size="xs"
                                />
                            </IconBox>
                        </Box>
                    </Tooltip>
                )}
                {count > 2 && (
                    <Tooltip
                        content={
                            <Box
                                sx={{
                                    maxHeight: "150px",
                                }}
                            >
                                {/* Used this instead of line clamp to also have custom text instead of ellipsis*/}
                                {users.slice(2).map((user, index) => {
                                    if (index <= 8) {
                                        return (
                                            <Box
                                                key={user.email}
                                                sx={{
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    maxWidth: "100%",
                                                }}
                                            >
                                                {user.email}
                                            </Box>
                                        );
                                    } else if (index === 9) {
                                        return (
                                            <Box key={user.email}>
                                                + {users.length - 11} more
                                            </Box>
                                        );
                                    }
                                    return null;
                                })}
                            </Box>
                        }
                        style={{
                            width: "100%",
                            height: "100%",
                        }}
                    >
                        <Box
                            sx={{
                                fontSize: "10px",
                                fontWeight: 400,
                                color: "var(--proda-black)",
                                borderRadius: "50%",
                                boxShadow: "1px 2px 6px rgba(6, 10, 22, 0.25)",
                                backgroundColor: "var(--proda-yellow-20)",
                                marginLeft: "-3px",
                                width: "24px",
                                height: "24px",
                                textAlign: "center",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                            }}
                        >
                            {`+${count - 2}`}
                        </Box>
                    </Tooltip>
                )}
            </Box>
        );
    };

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                padding: "4px 8px",
                borderRadius: "16px",
                cursor: "pointer",
            }}
        >
            {renderIcons()}
        </Box>
    );
};

export default UserGroupIndicator;
